import { INode, NodeStyleBase, SvgVisual, Visual } from '@yfiles/yfiles';
import {
  BusinessModel,
  BusinessProcessNodeType,
  ProcessNodeTag,
} from '../models/business-process.model';
interface Cache {
  type: BusinessProcessNodeType;
  color: string;
  fill: string;
  width: number;
  height: number;
}
export class BusinessProcessNodeStyle extends NodeStyleBase<Visual> {
  protected createVisual(_: any, node: INode): SvgVisual {
    const g = this.el('g');
    this.render(g, node);
    (g as any).__cache = this.cache(node);
    return new SvgVisual(g);
  }
  protected override updateVisual(
    context: any,
    oldVisual: Visual,
    node: INode,
  ): Visual {
    if (!(oldVisual instanceof SvgVisual))
      return this.createVisual(context, node);
    const g = oldVisual.svgElement as SVGGElement;
    g.setAttribute('transform', `translate(${node.layout.x} ${node.layout.y})`);
    const a = (g as any).__cache as Cache | undefined,
      b = this.cache(node);
    if (!a || JSON.stringify(a) !== JSON.stringify(b)) {
      g.replaceChildren();
      this.render(g, node);
      (g as any).__cache = b;
    }
    return oldVisual;
  }
  private render(g: SVGGElement, node: INode) {
    const d = (node.tag as ProcessNodeTag).process,
      { x, y, width: w, height: h } = node.layout;
    g.setAttribute('transform', `translate(${x} ${y})`);
    if (d.type === 'action') this.action(g, w, h, d.model);
    else if (d.type === 'output')
      this.surface(
        g,
        this.poly([
          [20, 2],
          [w - 20, 2],
          [w - 2, h / 2],
          [w - 20, h - 2],
          [20, h - 2],
          [2, h / 2],
        ]),
        d.model,
      );
    else if (d.type === 'decision')
      this.surface(
        g,
        this.poly([
          [w / 2, 2],
          [w - 2, h / 2],
          [w / 2, h - 2],
          [2, h / 2],
        ]),
        d.model,
      );
    else if (d.type === 'trigger')
      this.surface(
        g,
        this.poly([
          [30, 2],
          [w - 30, 2],
          [w - 2, h - 2],
          [2, h - 2],
        ]),
        d.model,
      );
    else if (d.type === 'model') this.otherModel(g, w, h, d.model);
    else this.outcome(g, w, h, d.model, d.label);
  }
  // a model not shown: a stack of cards, dashed as it is outside the graph
  private otherModel(g: SVGGElement, w: number, h: number, m: BusinessModel) {
    const card = (x: number, y: number, dashed: boolean) => {
      const r = this.el('rect');
      r.setAttribute('x', `${x}`);
      r.setAttribute('y', `${y}`);
      r.setAttribute('width', `${w - 10}`);
      r.setAttribute('height', `${h - 10}`);
      r.setAttribute('rx', '8');
      this.surface(g, r, m);
      r.setAttribute('stroke-width', '2');
      if (dashed) r.setAttribute('stroke-dasharray', '6 4');
    };
    card(8, 2, false);
    card(2, 8, true);
  }
  private action(g: SVGGElement, w: number, h: number, m: BusinessModel) {
    const r = this.el('rect');
    r.setAttribute('x', '2');
    r.setAttribute('y', '2');
    r.setAttribute('width', `${w - 4}`);
    r.setAttribute('height', `${h - 4}`);
    r.setAttribute('rx', '14');
    this.surface(g, r, m);
    const a = this.el('rect');
    a.setAttribute('x', '8');
    a.setAttribute('y', '14');
    a.setAttribute('width', '5');
    a.setAttribute('height', `${Math.max(0, h - 28)}`);
    a.setAttribute('rx', '3');
    a.setAttribute('fill', m.color);
    g.appendChild(a);
    const s = this.el('line');
    s.setAttribute('x1', '20');
    s.setAttribute('x2', '20');
    s.setAttribute('y1', '14');
    s.setAttribute('y2', `${h - 14}`);
    s.setAttribute('stroke', m.color);
    s.setAttribute('stroke-width', '1');
    s.setAttribute('opacity', '.18');
    g.appendChild(s);
  }
  private outcome(g: SVGGElement, w: number, h: number, m: BusinessModel, label = '') {
    const cx = w / 2,
      cy = h / 2,
      o = Math.min(w, h) / 2 - 4,
      i = o * 0.63,
      p: [number, number][] = [];
    for (let n = 0; n < 16; n++) {
      const r = n % 2 === 0 ? o : i,
        a = -Math.PI / 2 + (n * Math.PI) / 8;
      p.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
    }
    this.surface(g, this.poly(p), m);
    // the dot sits just above the outcome's name, which is centred and wraps in the star (as the label service and
    // the sizing service lay it out: 12px text in the star less 48 each way, about 6.5 a character and 15 a line), and
    // no higher than the star's top point
    const r = Math.min(w, h) * 0.04;
    const perLine = Math.max(1, Math.floor((w - 48) / 6.5));
    let lines = 1, used = 0;
    for (const word of label.trim().split(/\s+/).filter((x) => x)) {
      const needed = used ? word.length + 1 : word.length;
      if (used + needed > perLine) { lines++; used = word.length; } else used += needed;
    }
    const textHeight = Math.min(lines * 15, h - 48);
    const highest = cy - (i + (o - i) * 0.35);
    const c = this.el('circle');
    c.setAttribute('cx', `${cx}`);
    c.setAttribute('cy', `${Math.max(highest, cy - textHeight / 2 - 4 - r)}`);
    c.setAttribute('r', `${r}`);
    c.setAttribute('fill', m.color);
    g.appendChild(c);
  }
  private surface(g: SVGGElement, e: SVGElement, m: BusinessModel) {
    e.setAttribute('fill', m.fill);
    e.setAttribute('stroke', m.color);
    e.setAttribute('stroke-width', '3');
    e.setAttribute('stroke-linejoin', 'round');
    g.appendChild(e);
  }
  private poly(points: [number, number][]): SVGPolygonElement {
    const p = this.el('polygon');
    p.setAttribute('points', points.map(([x, y]) => `${x},${y}`).join(' '));
    return p;
  }
  private el<K extends keyof SVGElementTagNameMap>(
    name: K,
  ): SVGElementTagNameMap[K] {
    return document.createElementNS('http://www.w3.org/2000/svg', name);
  }
  private cache(n: INode): Cache {
    const d = (n.tag as ProcessNodeTag).process;
    return {
      type: d.type,
      color: d.model.color,
      fill: d.model.fill,
      width: n.layout.width,
      height: n.layout.height,
    };
  }
}
